import { isSameDay } from 'date-fns';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import {
  HorizontalFlex,
  VerticalFlex,
} from '@components/RecordCard/RecordCard.style';

import { useIsMobile } from '@hooks/useIsMobile';

import type { GenericRecord } from '@models/generic-record';

import { isValidDate } from '@utils/date';
import { formatDate, formatFacility } from '@utils/format';

import RecordTimelineCard from '../RecordTimelineCard/RecordTimelineCard';
import {
  RecordTimelineDate,
  RecordTimelineBlockDiv,
  LocationH2Icon,
  LocationH2,
} from './RecordTimelineBlock.style';

const messages = defineMessages({
  today: {
    id: 'recordTimelineBlock.today',
    defaultMessage: 'Today',
  },
  unknownDate: {
    id: 'dates.unknownDate',
    defaultMessage: 'Unknown Date',
  },
});

const RecordTimelineBlock: React.FC<{
  /**
   * Date to show on the timeline card header
   */
  date: Date | string;
  /**
   * Records to show on the timeline
   */
  records: GenericRecord[];
  /**
   * Format to display the date in
   */
  dateFormat?: string;
  /**
   * Should the date be flexed to the end?
   */
  flexEnd?: boolean;
}> = ({
  date = new Date(),
  records = [],
  dateFormat = 'MMMM D',
  flexEnd = false,
}) => {
  const { formatMessage } = useIntl();
  const mobile = useIsMobile('md');

  let isToday = false;
  let dateContent = formatDate({
    date: new Date(date),
    formatter: dateFormat,
  });
  if (!isValidDate(new Date(date))) {
    dateContent = 'Unknown Date';
  } else if (dateContent) {
    isToday = isSameDay(new Date(date), new Date());
    dateContent = isToday ? formatMessage(messages.today) : dateContent;
  } else {
    dateContent = date as string;
  }

  return (
    <RecordTimelineBlockDiv>
      {!mobile && (
        <RecordTimelineDate flexEnd={flexEnd}>{dateContent}</RecordTimelineDate>
      )}
      {records.map((record) => {
        const { id, route, surveyId, title, viewedStatus } = record;
        const facility = formatFacility(
          record.facilityName,
          record.facilityCity,
          '',
          record.facilityCountry
        );
        return (
          <RecordTimelineCard
            date={dateContent}
            key={id}
            noteDisplay={record.hasNotes}
            onClick={() => {
              if (route) {
                navigate(route);
              }
            }}
            surveyId={surveyId}
            title={title}
            viewed={viewedStatus}
          >
            {facility || record.hcpName ? (
              <HorizontalFlex marginBottom={0} marginTop={0}>
                <VerticalFlex alignItems="flex-start">
                  {facility ? (
                    <HorizontalFlex marginBottom={0} marginTop={0}>
                      <LocationH2Icon>
                        <Icon
                          color="warning.main"
                          name="location-dot"
                          type="fas"
                        />
                      </LocationH2Icon>
                      <LocationH2 style={{ whiteSpace: 'normal' }}>
                        {facility}
                      </LocationH2>
                    </HorizontalFlex>
                  ) : null}
                  {record.hcpName ? (
                    <HorizontalFlex marginBottom={0} marginTop={0}>
                      <LocationH2Icon>
                        <Icon
                          color="warning.main"
                          name="user-doctor"
                          type="fas"
                        />
                      </LocationH2Icon>
                      <LocationH2 style={{ whiteSpace: 'normal' }}>
                        {record.hcpName}
                      </LocationH2>
                    </HorizontalFlex>
                  ) : null}
                </VerticalFlex>
              </HorizontalFlex>
            ) : null}
          </RecordTimelineCard>
        );
      })}
    </RecordTimelineBlockDiv>
  );
};

export default RecordTimelineBlock;
